import React, {useEffect, useMemo, useRef} from "react";
import {NEURON_EVENTS} from "../config/eventTypes";
import {useParams, useNavigate} from "react-router-dom";
import { useStories } from "../hooks/useStories";
import {handleAssetClick} from "../utils";
import {FaRegFrown, FaArrowLeft} from "react-icons/fa";
import SimilarContentRail from "./SimilarContentRail";
import CommentsSection from "./CommentsSection";
import {Button} from "./ui/button";
import {trackNeuronEvent} from "../utils/trackNeuronEvent";
import {
  findStoryByRouteParam,
  getStoryRouteId,
} from "../utils/storyIdentity";
import {getNeuronItemId} from "../utils/getNeuronItemId";
// import {useDispatchEvent} from "../hooks/useDispatchEvent"; // Optional, explained below

// This ID must match the event configured in Neuron so the backend accepts
// the tracking payload. The previous placeholder ID of `1` meant the SDK was
// logging against an event that doesn't exist, so nothing was recorded.

const ArticlePage = ({userEmail}) => {
const {id} = useParams();
const navigate = useNavigate();
const {stories, loading: storiesLoading} = useStories();
const localStory = findStoryByRouteParam(stories, id);
const trackedStoryIdsRef = useRef(new Set());

// Fall back to sessionStorage for recommended items not in local stories
const story = useMemo(() => {
  if (localStory) return localStory;

  try {
    const cached = sessionStorage.getItem(`neuron:article:${id}`);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}, [id, localStory]);

const storyId = useMemo(() => getStoryRouteId(story), [story]);


  useEffect(() => {
    if (!storyId || !userEmail) return;
    if (trackedStoryIdsRef.current.has(storyId)) return;

    trackedStoryIdsRef.current.add(storyId);

    const sendViewEvent = async () => {
      try {
        const neuronItemId = getNeuronItemId(story) || getNeuronItemId({id: storyId});
        if (!neuronItemId) return;

        await trackNeuronEvent({
          itemId: neuronItemId,
          userId: userEmail,
          eventId: NEURON_EVENTS.viewed,
        });

        // Dispatch an event for the notification system
        window.dispatchEvent(
          new CustomEvent("neuronEventLogged", {
            detail: {
              message: "Article view event logged.",
              status: "success",
            },
          })
        );
      } catch (err) {
        console.error("💥 Error sending view event:", err);
        trackedStoryIdsRef.current.delete(storyId);
        window.dispatchEvent(
          new CustomEvent("neuronEventLogged", {
            detail: {
              message: "Failed to log article view.",
              status: "error",
            },
          })
        );

      }
    };

    void sendViewEvent();
  }, [story, storyId, userEmail]);

  useEffect(() => {
    if (!storyId) return;

    const storedUUIDs = JSON.parse(localStorage.getItem("viewedUUIDs")) || [];
    if (!storedUUIDs.includes(storyId)) {
      const updatedUUIDs = [...storedUUIDs, storyId];
      localStorage.setItem("viewedUUIDs", JSON.stringify(updatedUUIDs));
    }
  }, [storyId]);

  if (storiesLoading) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20">
        <p className="text-body text-ink-tertiary">Setting the page...</p>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20">
        <FaRegFrown className="text-5xl text-gray-500 mb-4" />
        <p className="font-serif text-h2 font-bold text-ink">We can't find that story.</p>
        <div className="flex items-center mb-4 pt-10">
          <button
            className="inline-flex items-center gap-1.5 font-sans text-meta font-semibold uppercase tracking-[0.08em] text-ink-tertiary hover:text-ink"
            onClick={() => navigate("/")}
          >
            <FaArrowLeft className="mr-2" /> Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <article className="container py-8">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-1.5 font-sans text-meta font-semibold uppercase tracking-[0.08em] text-ink-tertiary transition-colors duration-150 ease-out hover:text-ink"
      >
        <span aria-hidden="true">&#8592;</span> Back
      </button>

      <div className="mx-auto max-w-[42rem]">
        <header className="border-b border-rule pb-6">
          {story.aiCategorization ? (
            <p className="mb-2 font-sans text-kicker font-semibold uppercase tracking-[0.09em] text-masthead">
              {story.aiCategorization}
            </p>
          ) : null}

          <h1 className="font-serif text-h1 font-bold tracking-[-0.021em] text-ink sm:text-lede">
            {story.originalTitle}
          </h1>

          {story.summary || story.aiSummary ? (
            <p className="mt-4 text-standfirst text-ink-secondary">
              {story.summary || story.aiSummary}
            </p>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="font-sans text-meta text-ink-muted">
              {[story.source, story.timeToRead].filter(Boolean).join(" \u00b7 ")}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAssetClick(story.aiEmbeddingValue, navigate)}
            >
              Upvote
            </Button>
          </div>
        </header>

        <img
          src={story.image}
          alt=""
          className="photo-inset mt-6 w-full rounded-sm"
        />

        {/* The engine's own reading of the piece, in blue pencil. */}
        {story.aiSummary ? (
          <aside className="mt-8 border-l-2 border-pencil bg-pencil-muted/50 py-3 pl-4 pr-3">
            <p className="mb-1 font-sans text-kicker font-semibold uppercase tracking-[0.09em] text-pencil">
              Summary
            </p>
            <p className="text-body text-ink-secondary">{story.aiSummary}</p>
          </aside>
        ) : null}

        {/* Body copy: one column, comfortable measure, serif. */}
        <div
          className="article-body mt-8"
          dangerouslySetInnerHTML={{__html: story.aiHtmlDescription}}
        />
      </div>

      {/* Comments */}
      <div className="mx-auto max-w-[42rem]">
        <CommentsSection
          articleId={getStoryRouteId(story)}
          userEmail={userEmail}
        />
      </div>

      {/* Similar Content */}
      <SimilarContentRail
        articleId={getStoryRouteId(story)}
        userEmail={userEmail}
      />
    </article>
  );
};

export default ArticlePage;
